// SignPage.js
// Public customer signing page reached from the SMS/email link
// (/sign/:token). Shows the job summary and the resolved service terms, then
// captures the customer's name, agreement, and drawn signature.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Checkbox, FormControlLabel, Divider, Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import FittedSignatureCanvas from '../components/RateAgreement/FittedSignatureCanvas';
import { useOrg } from '../context/OrgContext';

const API = process.env.REACT_APP_API_URL || '';
const PRIMARY = '#1a237e';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');

const vehicleLabel = (vehicle) => {
  if (!vehicle) return null;
  const text = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  return vehicle.color ? `${text} (${vehicle.color})` : text;
};

const SignPage = () => {
  const { name, legalName, logoUrl, supportEmail, supportPhone } = useOrg();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { job, terms, status, signerName, signedAt }
  const [signerName, setSignerName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const signatureRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/sign/${token}`);
        if (cancelled) return;
        setData(res.data);
        setSignerName(res.data?.job?.customerName || '');
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'This signing link is not valid.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    setFormError(null);
    if (!signerName.trim()) { setFormError('Please enter your name.'); return; }
    if (!agreed) { setFormError('Please confirm that you agree to the terms.'); return; }
    if (!signatureRef.current || signatureRef.current.isEmpty()) { setFormError('Please sign in the box above.'); return; }

    setSubmitting(true);
    try {
      const signatureData = signatureRef.current.toDataURL('image/png');
      const res = await axios.post(`${API}/api/sign/${token}`, { signatureData, signerName: signerName.trim() });
      setData((prev) => ({ ...prev, status: 'signed', signerName: signerName.trim(), signedAt: res.data?.signedAt || new Date().toISOString() }));
    } catch (err) {
      if (err.response?.status === 410) {
        setData((prev) => ({ ...prev, status: 'expired' }));
      } else {
        setFormError(err.response?.data?.message || 'Could not save your signature. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <Paper elevation={3} sx={{ bgcolor: PRIMARY, color: '#fff', p: { xs: 1.5, sm: 2.5 }, mb: 2, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={logoUrl || '/images/logo.png'} alt={name} sx={{ height: { xs: 36, sm: 52 }, mr: 1.5 }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.4rem' }, lineHeight: 1.2 }}>
            {legalName.toUpperCase()}
          </Typography>
          <Typography variant="body2">Service Authorization</Typography>
        </Box>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        {header}
        <Alert severity="error">{error || 'This signing link is not valid.'}</Alert>
      </Container>
    );
  }

  const { job, terms, status } = data;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
      {header}

      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h6" sx={{ color: PRIMARY, fontWeight: 600, mb: 1 }}>Your service</Typography>
        <Stack spacing={0.5}>
          <Typography variant="body1"><strong>Service:</strong> {job.service}</Typography>
          <Typography variant="body2"><strong>Order #:</strong> {job.po}</Typography>
          {job.companyName && <Typography variant="body2"><strong>Provided by:</strong> {job.companyName}</Typography>}
          {vehicleLabel(job.vehicle) && <Typography variant="body2"><strong>Vehicle:</strong> {vehicleLabel(job.vehicle)}</Typography>}
          {job.serviceLocation?.city && (
            <Typography variant="body2"><strong>Location:</strong> {job.serviceLocation.city}{job.serviceLocation.state ? `, ${job.serviceLocation.state}` : ''}</Typography>
          )}
        </Stack>
      </Paper>

      {status === 'expired' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This signing link has expired or was already used. Please ask your driver or dispatcher to send a new one.
        </Alert>
      )}

      {status === 'signed' && (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
          <Typography variant="h6" gutterBottom>Thank you, your signature has been recorded.</Typography>
          <Typography variant="body2" color="text.secondary">
            Signed by {data.signerName || job.customerName}{data.signedAt ? ` on ${formatDate(data.signedAt)}` : ''}.
          </Typography>
        </Paper>
      )}

      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="h6" sx={{ color: PRIMARY, fontWeight: 600 }}>{terms.title}</Typography>
        <Typography variant="caption" color="text.secondary">Please read these terms before signing.</Typography>
        <Box
          sx={{
            mt: 1.5, p: 1.5, maxHeight: { xs: 260, sm: 340 }, overflowY: 'auto',
            border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa',
            whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.5
          }}
        >
          {terms.body}
        </Box>
      </Paper>

      {status === 'pending' && (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Typography variant="h6" sx={{ color: PRIMARY, fontWeight: 600, mb: 1 }}>Sign here</Typography>
          <TextField
            label="Your full name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            fullWidth
            required
            margin="normal"
            inputProps={{ maxLength: 120 }}
          />
          <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>Draw your signature with your finger or mouse:</Typography>
          <Box sx={{ position: 'relative', width: '100%', height: 200, border: '1px solid #bdbdbd', borderRadius: 1, bgcolor: '#fff' }}>
            <FittedSignatureCanvas ref={signatureRef} height={200} />
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button size="small" startIcon={<ClearIcon />} onClick={() => signatureRef.current?.clear()}>Clear</Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
            label="I have read and agree to these terms"
          />
          {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ mt: 2, bgcolor: PRIMARY, '&:hover': { bgcolor: '#2a3490' } }}
          >
            {submitting ? 'Saving…' : 'Sign'}
          </Button>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
        {`${legalName} · ${supportEmail} · ${supportPhone}`}
      </Typography>
    </Container>
  );
};

export default SignPage;
